import React, { useMemo, useState, useEffect } from 'react';
import { Form, Input, Button, Card, Typography, Layout } from 'antd';
import { LockOutlined, SafetyOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { App as AntdApp } from 'antd';
import { useAuthApi } from '../../../api/auth/auth';
import bg from '../../../assets/images/background/background_login.jpg';
import { appVersion } from '../../../config/config';
import LanguageSwitcher from '../../../components/auth/LanguageSwitcher';

const { Title } = Typography;
const { Content, Footer } = Layout;

interface ResetPasswordFormValues {
  password: string;
  confirmPassword: string;
}

const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const navigate = useNavigate();
  const { message } = AntdApp.useApp();
  const { resetPasswordWithToken } = useAuthApi();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const token = useMemo(() => new URLSearchParams(search).get('token') || '', [search]);

  const palette = useMemo(
    () => ({
      primary: '#2D9CC9',
      secondary: '#8BBE1E',
      background: '#031323',
      surface: '#f5f9ff',
      outline: 'rgba(41, 146, 191, 0.22)',
      textPrimary: '#0c1324',
      textMuted: 'rgba(14, 21, 34, 0.62)',
    }),
    []
  );

  useEffect(() => {
    if (!token) {
      message.error(t('auth.resetPassword.invalidToken'));
      setTimeout(() => navigate('/login'), 2000);
    }
  }, [token, message, navigate, t]);

  const calculatePasswordStrength = (password: string): number => {
    if (!password) return 0;
    let strength = 0;
    if (password.length >= 6) strength += 1;
    if (password.length >= 8) strength += 1;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 1;
    if (/\d/.test(password)) strength += 1;
    if (/[^a-zA-Z\d]/.test(password)) strength += 1;
    return Math.min(strength, 4);
  };

  const getPasswordStrengthColor = (strength: number): string => {
    if (strength === 0) return palette.textMuted;
    if (strength === 1) return '#ff4d4f';
    if (strength === 2) return '#faad14';
    if (strength === 3) return '#52c41a';
    return palette.secondary;
  };

  const getPasswordStrengthText = (strength: number): string => {
    if (strength === 0) return '';
    if (strength === 1) return t('auth.resetPassword.strength.weak');
    if (strength === 2) return t('auth.resetPassword.strength.medium');
    if (strength === 3) return t('auth.resetPassword.strength.strong');
    return t('auth.resetPassword.strength.veryStrong');
  };

  const onFinish = async (values: ResetPasswordFormValues) => {
    if (!token) {
      message.error(t('auth.resetPassword.invalidToken'));
      return;
    }
    setLoading(true);
    try {
      await resetPasswordWithToken(token, values.password);
      message.success(t('auth.resetPassword.success'));
      setTimeout(() => navigate('/login'), 1500);
    } catch (e: any) {
      message.error(e?.response?.data?.message ?? t('auth.resetPassword.error'));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return null;
  }

  return (
    <Layout
      style={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        background: palette.background,
      }}
    >
      {/* Language Switcher - Top Right */}
      <div
        style={{
          position: 'absolute',
          top: 24,
          right: 24,
          zIndex: 1000,
        }}
      >
        <LanguageSwitcher variant="dark" />
      </div>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `linear-gradient(135deg, rgba(41,146,191,0.24), rgba(8,44,68,0.55)), url("${bg}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'brightness(0.92)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          width: 360,
          height: 360,
          borderRadius: '50%',
          top: '-120px',
          left: '-120px',
          background: 'radial-gradient(circle, rgba(139,190,30,0.36), transparent 70%)',
          filter: 'blur(16px)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          width: 420,
          height: 420,
          borderRadius: '50%',
          bottom: '-180px',
          right: '-120px',
          background: 'radial-gradient(circle, rgba(41,146,191,0.32), transparent 70%)',
          filter: 'blur(22px)',
        }}
      />
      <Content
        style={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '40px 16px',
          position: 'relative',
        }}
      >
        <Card
          bordered={false}
          style={{
            backgroundColor: palette.surface,
            borderRadius: 32,
            margin: '20px',
            width: '100%',
            maxWidth: '440px',
            boxShadow: '0 28px 65px rgba(6,24,38,0.32)',
            backdropFilter: 'blur(16px)',
            border: `1px solid rgba(255,255,255,0.75)`,
            overflow: 'hidden',
          }}
          bodyStyle={{
            padding: '42px 36px',
            position: 'relative',
            zIndex: 2,
          }}
        >
          <span
            style={{
              position: 'absolute',
              inset: '12px',
              borderRadius: 22,
              background: 'linear-gradient(135deg, rgba(255,255,255,0.45), rgba(255,255,255,0.15))',
              border: `1px solid ${palette.outline}`,
              zIndex: -1,
            }}
          />
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${palette.primary}, ${palette.secondary})`,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 16,
                boxShadow: '0 8px 24px rgba(45,156,201,0.3)',
              }}
            >
              <SafetyOutlined style={{ fontSize: 28, color: '#ffffff' }} />
            </div>
            <Title
              level={2}
              style={{
                textAlign: 'center',
                marginBottom: 12,
                color: palette.textPrimary,
                fontWeight: 800,
              }}
            >
              {t('auth.resetPassword.title')}
            </Title>
            <Typography.Paragraph
              style={{
                textAlign: 'center',
                marginBottom: 28,
                color: palette.textMuted,
                fontSize: 15,
                lineHeight: 1.6,
              }}
            >
              {t('auth.resetPassword.subtitle')}
            </Typography.Paragraph>
          </div>
          <Form
            form={form}
            name="reset_password_form"
            onFinish={onFinish}
            layout="vertical"
            style={{ maxWidth: '100%' }}
          >
            <Form.Item
              name="password"
              rules={[
                { required: true, message: t('auth.resetPassword.passwordRequired') },
                { min: 6, message: t('auth.resetPassword.passwordMinLength') },
              ]}
              style={{ marginBottom: 18 }}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder={t('auth.resetPassword.passwordPlaceholder')}
                size="large"
                onChange={(e) => setPasswordStrength(calculatePasswordStrength(e.target.value))}
                style={{
                  borderRadius: 18,
                  padding: '14px 16px',
                  background: '#ffffff',
                  border: `1px solid ${palette.outline}`,
                  boxShadow: '0 6px 18px rgba(41,146,191,0.12)',
                }}
              />
            </Form.Item>
            {passwordStrength > 0 && (
              <div style={{ marginBottom: 18, marginTop: -8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <div style={{ flex: 1, height: 4, background: palette.outline, borderRadius: 2, overflow: 'hidden' }}>
                    <div
                      style={{
                        width: `${(passwordStrength / 4) * 100}%`,
                        height: '100%',
                        background: `linear-gradient(90deg, ${getPasswordStrengthColor(passwordStrength)}, ${palette.secondary})`,
                        transition: 'all 0.3s ease',
                      }}
                    />
                  </div>
                  <Typography.Text
                    style={{
                      fontSize: 12,
                      color: getPasswordStrengthColor(passwordStrength),
                      fontWeight: 600,
                      minWidth: 60,
                    }}
                  >
                    {getPasswordStrengthText(passwordStrength)}
                  </Typography.Text>
                </div>
              </div>
            )}
            <Form.Item
              name="confirmPassword"
              dependencies={['password']}
              rules={[
                { required: true, message: t('auth.resetPassword.confirmPasswordRequired') },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(t('auth.resetPassword.passwordMismatch')));
                  },
                }),
              ]}
              style={{ marginBottom: 26 }}
            >
              <Input.Password
                prefix={<CheckCircleOutlined />}
                placeholder={t('auth.resetPassword.confirmPasswordPlaceholder')}
                size="large"
                style={{
                  borderRadius: 18,
                  padding: '14px 16px',
                  background: '#ffffff',
                  border: `1px solid ${palette.outline}`,
                  boxShadow: '0 6px 18px rgba(41,146,191,0.12)',
                }}
              />
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                loading={loading}
                style={{
                  borderRadius: 999,
                  height: 52,
                  border: 'none',
                  background: `linear-gradient(135deg, ${palette.primary}, ${palette.secondary})`,
                  boxShadow: '0 24px 45px rgba(45,156,201,0.35)',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                }}
              >
                {loading ? t('auth.resetPassword.submitting') : t('auth.resetPassword.submit')}
              </Button>
            </Form.Item>
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Typography.Text style={{ color: palette.textMuted, fontSize: 13 }}>
                {t('auth.resetPassword.rememberPassword')}{' '}
                <a
                  href="/login"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/login');
                  }}
                  style={{
                    color: palette.primary,
                    fontWeight: 600,
                    textDecoration: 'none',
                  }}
                >
                  {t('auth.login.submit')}
                </a>
              </Typography.Text>
            </div>
          </Form>
        </Card>
      </Content>
      <Footer style={{ background: 'transparent', position: 'relative', zIndex: 1 }}>
        <div
          style={{
            margin: '0 auto',
            padding: '10px 24px',
            borderRadius: 999,
            background: 'rgba(7, 25, 40, 0.82)',
            border: '1px solid rgba(255,255,255,0.32)',
            color: 'rgba(255,255,255,0.92)',
            fontSize: 12,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            maxWidth: 360,
          }}
        >
          {t('auth.login.footer', { year: new Date().getFullYear(), version: appVersion || 'dev' })}
        </div>
      </Footer>
    </Layout>
  );
};

export default ResetPassword;

